// Immutable structural source mirror for #7414 coherent read r3 (2026-10-09).
// Canonical source SHA256: 9fa2f6d54f749a5ae4a410558d2c3f8f16ca01da8f1b0605cbe4413ee22baa37.
// Source receipt SHA256: b16f784bab09284f398680cfe53e344f46f1ce9000b96d7ff15dd76dd5bd1ed7.
// Not generated Database.Functions metadata or live-schema qualification.
/** Wire contract for persisted, server-atomic batch RPCs (#7414).
 * UI callers retain the returned revision and complete retained membership.
 * An operation UUID identifies one immutable request; it is not a retry loop.
 */
import type { Json } from "@/integrations/supabase/types";

export type ContributionBatchStatus = "open" | "closed" | "deleted" | "review_required";

export interface ContributionBatchHeader {
  id: string;
  organization_id: string;
  legacy_batch_id: string;
  batch_date: string | null;
  event_name: string | null;
  status: ContributionBatchStatus;
  managed: boolean;
  revision: number;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  deleted_at: string | null;
}

export interface RetainedBatchMember {
  id: string;
  person_id: string | null;
  date: string;
  batch_date: string | null;
  deleted_at: string | null;
  amount: number;
  /** Exact stored value; the read must not normalize a legacy method. */
  method: string;
  check_number: string | null;
  notes: string | null;
  campus_id: string | null;
  status: string;
  revision: string;
  auto_post_to_gl: boolean | null;
  /** All canonical allocation rows remain part of the financial digest. */
  allocation_count: number;
  /** Operational allocation count scoped to the header organization. */
  organization_allocation_count: number;
  has_allocations: boolean;
  edit_eligibility: ContributionBatchMemberEditEligibility;
}

/** Read-only guidance as of the same statement snapshot, never a write lease. */
export interface ContributionBatchMemberEditEligibility {
  eligible: boolean;
  blockers: string[];
  correction_route: ContributionBatchCorrectionRoute | null;
  /** Existing allocations prevent an amount change, not every notes edit. */
  amount_change_blocked_by_allocations: boolean;
  /** Submitted person/campus/method/check/amount values are validated again. */
  value_dependent_checks_required: true;
  /** Exact Finance read-side facts, independently rechecked by the writer. */
  field_restrictions: ContributionBatchMemberFieldRestrictions;
}

export interface ContributionBatchMemberFieldRestrictions {
  amount_change_requires_allocation_replacement: boolean;
  current_person_valid: boolean;
  current_campus_valid: boolean;
  current_method_valid: boolean;
  current_check_number_valid: boolean;
  current_status_valid: boolean;
  /** Existing stripe/recurring labels can restrict edits to notes only. */
  non_notes_change_requires_source_workflow: boolean;
  allowed_methods: ["cash", "check", "other"];
  value_dependent_checks_required: true;
}

export type ContributionBatchCorrectionRoute =
  | "admin_ownership_review"
  | "connected_source"
  | "individual_finance_lifecycle";

export interface ContributionBatchEligibility {
  eligible: boolean;
  header_blockers: string[];
  member_blockers: Array<{
    contribution_id: string;
    blockers: string[];
    correction_route: ContributionBatchCorrectionRoute;
  }>;
}

export interface ContributionBatchSnapshot {
  header: ContributionBatchHeader;
  /** Sorted complete retained membership, including deleted gifts. */
  complete_member_ids: string[];
  members: RetainedBatchMember[];
  eligibility: ContributionBatchEligibility;
  /** Values, hashes and read predicates share one PostgreSQL statement snapshot. */
  snapshot_semantics: "statement";
}

export function readContributionBatchArgs(organizationId: string, batchId: string) {
  return { p_organization_id: organizationId, p_batch_id: batchId };
}

export interface ContributionBatchReceipt {
  batch_id: string;
  revision: number;
  status: ContributionBatchStatus;
  contribution_ids: string[];
  created_count?: number;
  updated_count?: number;
  deleted_count?: number;
}

export interface ContributionBatchCompareAndSwap {
  p_organization_id: string;
  p_batch_id: string;
  p_expected_revision: number;
  p_complete_member_ids: string[];
  p_reason: string;
  p_operation_id: string;
}

export interface ManualBatchEntry {
  person_id?: string | null;
  donor_name?: string;
  amount: number;
  method: "cash" | "check" | "other";
  check_number?: string;
  notes?: string;
  campus_id?: string | null;
  allocations: Array<{
    fund_id: string;
    amount: number;
    campus?: string;
    program?: string;
    pledge_id?: string;
    non_deductible_amount?: number;
  }>;
}

export interface CreateContributionBatchRequest {
  p_organization_id: string;
  p_batch_date: string;
  p_event_name: string | null;
  p_entries: ManualBatchEntry[];
  p_operation_id: string;
  p_auto_post_to_gl?: boolean;
}

export interface ChangeContributionBatchDateRequest extends ContributionBatchCompareAndSwap {
  p_new_date: string;
}

export interface AppendContributionBatchRequest extends ContributionBatchCompareAndSwap {
  p_entries: ManualBatchEntry[];
  p_auto_post_to_gl?: boolean;
}

/** Explicit JSON transport preserves only the dedicated manual-entry fields. */
function manualEntriesJson(entries: ManualBatchEntry[]): Json {
  return entries.map((entry) => ({
    amount: entry.amount,
    method: entry.method,
    ...(entry.person_id !== undefined ? { person_id: entry.person_id } : {}),
    ...(entry.donor_name !== undefined ? { donor_name: entry.donor_name } : {}),
    ...(entry.check_number !== undefined ? { check_number: entry.check_number } : {}),
    ...(entry.notes !== undefined ? { notes: entry.notes } : {}),
    ...(entry.campus_id !== undefined ? { campus_id: entry.campus_id } : {}),
    allocations: entry.allocations.map((allocation) => ({
      fund_id: allocation.fund_id,
      amount: allocation.amount,
      ...(allocation.campus !== undefined ? { campus: allocation.campus } : {}),
      ...(allocation.program !== undefined ? { program: allocation.program } : {}),
      ...(allocation.pledge_id !== undefined ? { pledge_id: allocation.pledge_id } : {}),
      ...(allocation.non_deductible_amount !== undefined
        ? { non_deductible_amount: allocation.non_deductible_amount }
        : {}),
    })),
  }));
}

export function createContributionBatchArgs(request: CreateContributionBatchRequest) {
  return {
    p_organization_id: request.p_organization_id,
    p_batch_date: request.p_batch_date,
    // SQL normalizes both null and an empty event name to a retained null.
    // Generated RPC metadata represents this SQL-nullable argument as string.
    p_event_name: request.p_event_name ?? "",
    p_entries: manualEntriesJson(request.p_entries),
    p_auto_post_to_gl: request.p_auto_post_to_gl ?? false,
    p_operation_id: request.p_operation_id,
  };
}

export function appendContributionBatchArgs(request: AppendContributionBatchRequest) {
  return {
    p_organization_id: request.p_organization_id,
    p_batch_id: request.p_batch_id,
    p_expected_revision: request.p_expected_revision,
    p_complete_member_ids: request.p_complete_member_ids,
    p_reason: request.p_reason,
    p_operation_id: request.p_operation_id,
    p_entries: manualEntriesJson(request.p_entries),
    p_auto_post_to_gl: request.p_auto_post_to_gl ?? false,
  };
}

/** Selected review has a separate snapshot from complete retained membership. */
export interface ContributionReviewContext {
  selected_member_revisions: Record<string, string>;
  managed_batches: Array<{
    batch_id: string;
    expected_revision: number;
    complete_member_ids: string[];
    selected_member_ids: string[];
  }>;
}

export interface ReviewContributionBatchRequest extends ContributionBatchCompareAndSwap {
  p_selected_member_ids: string[];
  p_expected_selected_revisions: Record<string, string>;
}

export interface ContributionBatchReviewResult {
  receipt: ContributionBatchReceipt;
  /** Transient first-application rows. Replays return no rows or events. */
  reviewed_records: Json[];
  replayed: boolean;
}

export function reviewContributionBatchArgs(request: ReviewContributionBatchRequest) {
  const revisions: { [key: string]: Json } = {};
  for (const [id, revision] of Object.entries(request.p_expected_selected_revisions)) {
    revisions[id] = revision;
  }
  return {
    p_organization_id: request.p_organization_id,
    p_batch_id: request.p_batch_id,
    p_expected_revision: request.p_expected_revision,
    p_complete_member_ids: request.p_complete_member_ids,
    p_selected_member_ids: request.p_selected_member_ids,
    p_expected_selected_revisions: revisions,
    p_reason: request.p_reason,
    p_operation_id: request.p_operation_id,
  };
}

/** Inline header metadata editing; dates use the separate whole-batch RPC. */
export interface UpdateContributionBatchDetailsRequest extends ContributionBatchCompareAndSwap {
  p_event_name: string | null;
}
export function updateContributionBatchDetailsArgs(request: UpdateContributionBatchDetailsRequest) {
  return {
    p_organization_id: request.p_organization_id,
    p_batch_id: request.p_batch_id,
    p_expected_revision: request.p_expected_revision,
    p_complete_member_ids: request.p_complete_member_ids,
    p_event_name: request.p_event_name ?? "",
    p_reason: request.p_reason,
    p_operation_id: request.p_operation_id,
  };
}

/** Permitted inline fields. Batch dates/source/identity/status are server routes. */
export interface ContributionBatchMemberChanges {
  person_id?: string | null;
  amount?: number;
  method?: string;
  check_number?: string | null;
  notes?: string | null;
  campus_id?: string | null;
}
export interface UpdateContributionBatchMemberRequest extends ContributionBatchCompareAndSwap {
  p_contribution_id: string;
  p_expected_contribution_revision: string;
  p_changes: ContributionBatchMemberChanges;
}
export interface ContributionBatchMemberUpdateResult {
  receipt: ContributionBatchReceipt;
  updated_record: Json | null;
  replayed: boolean;
}
export function updateContributionBatchMemberArgs(request: UpdateContributionBatchMemberRequest) {
  const c = request.p_changes;
  const changes: { [key: string]: Json } = {
    ...(c.person_id !== undefined ? { person_id: c.person_id } : {}),
    ...(c.amount !== undefined ? { amount: c.amount } : {}),
    ...(c.method !== undefined ? { method: c.method } : {}),
    ...(c.check_number !== undefined ? { check_number: c.check_number } : {}),
    ...(c.notes !== undefined ? { notes: c.notes } : {}),
    ...(c.campus_id !== undefined ? { campus_id: c.campus_id } : {}),
  };
  return {
    p_organization_id: request.p_organization_id,
    p_batch_id: request.p_batch_id,
    p_expected_revision: request.p_expected_revision,
    p_complete_member_ids: request.p_complete_member_ids,
    p_contribution_id: request.p_contribution_id,
    p_expected_contribution_revision: request.p_expected_contribution_revision,
    p_changes: changes,
    p_reason: request.p_reason,
    p_operation_id: request.p_operation_id,
  };
}

/** Build an edit from ONE retained read, never from separately fetched hashes. */
export function updateContributionBatchMemberFromSnapshotArgs(
  snapshot: ContributionBatchSnapshot,
  contributionId: string,
  changes: ContributionBatchMemberChanges,
  reason: string,
  operationId: string,
) {
  const memberIds = snapshot.members.map((member) => member.id);
  if (snapshot.snapshot_semantics !== "statement"
    || !Number.isSafeInteger(snapshot.header.revision) || snapshot.header.revision < 1
    || memberIds.length !== new Set(memberIds).size
    || memberIds.length !== snapshot.complete_member_ids.length
    || memberIds.some((id, index) => id !== snapshot.complete_member_ids[index])) {
    throw new Error("Refresh the complete saved-batch snapshot before editing");
  }
  const member = snapshot.members.find((item) => item.id === contributionId);
  if (!member || !/^[0-9a-f]{64}$/.test(member.revision)) {
    throw new Error("Selected entry requires its revision from the same batch snapshot");
  }
  if (!member.edit_eligibility.eligible) {
    throw new Error("Selected entry requires the indicated ownership or financial correction route");
  }
  if (member.allocation_count < 0 || !Number.isSafeInteger(member.allocation_count)
    || member.has_allocations !== (member.allocation_count > 0)
    || member.organization_allocation_count < 0
    || member.organization_allocation_count > member.allocation_count
    || !Number.isSafeInteger(member.organization_allocation_count)
    || member.edit_eligibility.amount_change_blocked_by_allocations
      !== member.edit_eligibility.field_restrictions.amount_change_requires_allocation_replacement) {
    throw new Error("Refresh the selected entry's allocation facts before editing");
  }
  if (member.edit_eligibility.amount_change_blocked_by_allocations
    && changes.amount !== undefined && changes.amount !== member.amount) {
    throw new Error("Existing allocations must be resolved before changing the amount");
  }
  if (member.edit_eligibility.field_restrictions.non_notes_change_requires_source_workflow
    && (["person_id", "amount", "method", "check_number", "campus_id"] as const)
      .some((key) => changes[key] !== undefined)) {
    throw new Error("Non-notes changes require the indicated source workflow");
  }
  return updateContributionBatchMemberArgs({
    p_organization_id: snapshot.header.organization_id,
    p_batch_id: snapshot.header.id,
    p_expected_revision: snapshot.header.revision,
    p_complete_member_ids: [...snapshot.complete_member_ids],
    p_contribution_id: member.id,
    p_expected_contribution_revision: member.revision,
    p_changes: changes,
    p_reason: reason,
    p_operation_id: operationId,
  });
}
